/**
 * @module features/entity/entity-selector
 * @description Entity selection UI and navigation for all entity types
 */

import { ENTITY_TYPES } from './entity-types.js';
import { loadEntityFiles } from './entity-loader.js';
import { getDOMElements } from '../../ui/dom/dom-manager.js';
import { hideBreadcrumb, showEntityTypeBreadcrumb } from '../navigation/breadcrumb.js';
import { getFileId } from '../../core/router/url-router.js';
import { logger } from '../../utils/logger.js';
import { displayRecentItems } from '../navigation/recent-items.js';

/**
 * Show the unified entity selection screen
 * This is the main landing page with entity type cards
 *
 * @param {Object} user - Firebase user object
 */
export async function showProjectSelection(user) {
    try {
        const dom = getDOMElements;

        // Hide other sections
        dom.loadingSection.classList.add('hidden');
        dom.filesSection.classList.add('hidden');
        dom.errorMessage.classList.add('hidden');

        // Hide entity-specific search if visible
        const entitySearchContainer = document.querySelector('.entity-specific-search');
        if (entitySearchContainer) {
            entitySearchContainer.classList.add('hidden');
        }

        // Show unified search container
        const unifiedContainer = document.querySelector('.unified-search-container');
        if (unifiedContainer) {
            unifiedContainer.classList.remove('hidden');
        }

        // Hide breadcrumb when returning to selection
        hideBreadcrumb();

        // Show project selection
        dom.projectSelectionSection.classList.remove('hidden');

        // Initialize universal search (handles browse toggle state)
        // Note: This will be available when search module is integrated
        if (typeof window.initializeUniversalSearch === 'function') {
            window.initializeUniversalSearch(user);
        }

        // Set up entity type card click handlers
        setupEntityTypeCards(user);

        // Display recent items
        displayRecentItems();
    } catch (error) {
        logger.error('Error showing project selection:', error);
        showProjectError('Failed to initialize project selection');
    }
}

/**
 * Set up click handlers for entity type cards
 * @param {Object} user - Firebase user object
 */
export function setupEntityTypeCards(user) {
    const cards = document.querySelectorAll('.entity-type-card');
    cards.forEach(card => {
        card.addEventListener('click', () => {
            const entityType = card.dataset.type;
            const config = ENTITY_TYPES[entityType];

            if (config) {
                // Show entity-specific selector for all types
                showEntityTypeSelector(entityType, user);
            } else {
                logger.error('Unknown entity type:', entityType);
            }
        });
    });
}

/**
 * Show entity-specific selector with search interface
 * @param {string} entityType - Type of entity (projects, opportunities, leads, contacts)
 * @param {Object} user - Firebase user object
 */
export async function showEntityTypeSelector(entityType, user) {
    const config = ENTITY_TYPES[entityType];
    if (!config) {
        logger.error('Unknown entity type:', entityType);
        return;
    }

    const dom = getDOMElements;

    // Hide files section
    dom.filesSection.classList.add('hidden');

    // Show project selection section
    dom.projectSelectionSection.classList.remove('hidden');

    // Hide universal search UI, show entity-specific search
    const unifiedContainer = document.querySelector('.unified-search-container');
    const entitySearchContainer = document.querySelector('.entity-specific-search');

    if (unifiedContainer) unifiedContainer.classList.add('hidden');
    if (entitySearchContainer) {
        entitySearchContainer.classList.remove('hidden');

        // Clear previous search state
        const entitySearchInput = document.getElementById('entity-search-input');
        const entitySearchResults = document.getElementById('entity-search-results');
        const entityResultsContainer = document.getElementById('entity-results-container');
        const entitySearchError = document.getElementById('entity-search-error');

        if (entitySearchInput) entitySearchInput.value = '';
        if (entitySearchResults) entitySearchResults.classList.add('hidden');
        if (entityResultsContainer) entityResultsContainer.innerHTML = '';
        if (entitySearchError) entitySearchError.classList.add('hidden');

        // Update the UI with entity-specific labels
        const entitySearchIcon = document.getElementById('entity-search-icon');
        const entitySearchTitle = document.getElementById('entity-search-title');
        const entitySearchIntro = document.getElementById('entity-search-intro');
        const entitySearchBtnText = document.getElementById('entity-search-btn-text');

        if (entitySearchIcon) entitySearchIcon.textContent = config.icon;
        if (entitySearchTitle) {
            // Use searchTitle directly (no suffix needed)
            const titleText = config.searchTitle || `${config.labelPlural || `${config.label  }s`} Search`;
            entitySearchTitle.textContent = titleText;
        }

        // If custom search intro is provided, use it; otherwise use the template with type label
        if (entitySearchIntro) {
            if (config.searchIntro) {
                entitySearchIntro.textContent = config.searchIntro;
            } else {
                // Use template with embedded type label for standard entities
                entitySearchIntro.innerHTML = `Search for a <span id="entity-search-type-label">${config.singular}</span> to view archived files`;
            }
        }

        if (entitySearchBtnText) entitySearchBtnText.textContent = config.searchButtonText || `Search ${config.labelPlural || `${config.label  }s`}`;
        if (entitySearchInput) entitySearchInput.placeholder = `${config.searchPlaceholder} (minimum 3 characters)...`;

        // Show breadcrumb: Archive Home > [Entity Type]
        showEntityTypeBreadcrumb(entityType);

        // Initialize the entity search with selectEntity as callback
        if (typeof window.initializeEntitySearch === 'function') {
            window.initializeEntitySearch(entityType, user, selectEntity);
        }
    }
}

/**
 * Select an entity and load its files
 * Updates URL and loads entity data
 *
 * @param {string} entityType - Type of entity
 * @param {string} entityId - Entity ID
 * @param {Object} user - Firebase user object
 */
export async function selectEntity(entityType, entityId, user) {
    try {
        const config = ENTITY_TYPES[entityType];

        if (!config) {
            throw new Error(`Unknown entity type: ${entityType}`);
        }

        // Validate entityId
        if (!entityId || entityId === 'undefined' || entityId.trim() === '') {
            throw new Error(`Invalid ${entityType} ID provided`);
        }

        // Update URL without page reload
        const newUrl = new URL(window.location);
        newUrl.searchParams.set(config.paramKey, entityId);

        // Clear other entity params
        Object.values(ENTITY_TYPES).forEach(type => {
            if (type.paramKey !== config.paramKey) {
                newUrl.searchParams.delete(type.paramKey);
            }
        });

        history.pushState({[config.paramKey]: entityId}, '', newUrl);

        // Hide entity search section
        const entitySearchContainer = document.querySelector('.entity-specific-search');
        if (entitySearchContainer) entitySearchContainer.classList.add('hidden');

        // Hide project selection section
        const dom = getDOMElements;
        dom.projectSelectionSection.classList.add('hidden');

        // Load entity files (note: user is first parameter)
        const fileId = getFileId(); // Check if there's also a fileId parameter
        await loadEntityFiles(user, entityType, entityId, fileId);

    } catch (error) {
        logger.error(`Error selecting ${entityType}:`, error);
        const errorDiv = document.getElementById('entity-search-error');
        showEntitySearchError(`Failed to load ${entityType}: ${error.message}`, errorDiv, null);

        // Show entity search again on error
        const entitySearchContainer = document.querySelector('.entity-specific-search');
        if (entitySearchContainer) entitySearchContainer.classList.remove('hidden');
    }
}

/**
 * Legacy function for backward compatibility
 * Redirects to selectEntity
 *
 * @param {string} projectId - Project ID
 * @param {Object} user - Firebase user object
 * @deprecated Use selectEntity('projects', projectId, user) instead
 */
export async function selectProject(projectId, user) {
    return selectEntity('projects', projectId, user);
}

/**
 * Show error in the project selection screen
 * @param {string} message - Error message
 */
function showProjectError(message) {
    const errorDiv = document.getElementById('entity-search-error');
    if (errorDiv) {
        errorDiv.textContent = message;
        errorDiv.classList.remove('hidden');
    }
}

/**
 * Show entity search error message
 * @param {string} message - Error message
 * @param {HTMLElement} errorDiv - Error div element
 * @param {HTMLElement} resultsSection - Results section element
 */
function showEntitySearchError(message, errorDiv, resultsSection) {
    if (errorDiv) {
        errorDiv.textContent = message;
        errorDiv.classList.remove('hidden');
    }
    if (resultsSection) {
        resultsSection.classList.add('hidden');
    }
}

export default {
    showProjectSelection,
    setupEntityTypeCards,
    showEntityTypeSelector,
    selectEntity,
    selectProject
};
